import React from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Tooltip,
  ClickAwayListener,
  Menu,
  MenuItem,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import AddIcon from "@material-ui/icons/Add";
import DescriptionIcon from "@material-ui/icons/Description";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { apiPath } from "api";
import {
  generalAuthTokenHeader,
  multimediaAuthTokenHeader,
} from "__helpers/auth-header";
import moment from "moment";
import ConfirmModal from "components/Modal/modal";
import { userService } from "_services/user.service";

// Icons
import CrownIcon from "assets/img/icons/crown.svg";
import EyeIcon from "assets/img/icons/eye-icon.svg";
import DownloadIcon from "assets/img/icons/download-icon.svg";
import DeleteIconSvg from "assets/img/icons/delete-icon.svg";

import "./PatientDetailsPanel.css";
import ReferralSummary from "views/dpViews/SinglePatientDetail/ReferralSummary";

class PatientDetailsPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      patientNote: "",
      savingNote: false,
      uploadingDocument: false,
      deletingPatient: false,
      deletingDocument: false,
      deleteDocumentId: null,
      confirmDeleteOpen: false,
      confirmDeleteDocOpen: false,
      viewingSummary: false,
      summaryHtml: "",
      // States for loading
      loadingAISummary: false,
      loadingDocumentId: null, // Track which specific document is loading by ID
      tooltipAIInsightsOpen: false,
      exportMenuAnchor: null,
      showReferralSummary: false,
    };

    this.handleNoteChange = this.handleNoteChange.bind(this);
    this.handleSaveNote = this.handleSaveNote.bind(this);
    this.handleDocumentUpload = this.handleDocumentUpload.bind(this);
    this.handleDeletePatient = this.handleDeletePatient.bind(this);
    this.handleDeleteDocument = this.handleDeleteDocument.bind(this);
    this.handleViewSummary = this.handleViewSummary.bind(this);
    this.handleDownloadPdf = this.handleDownloadPdf.bind(this);
    this.handleViewDocument = this.handleViewDocument.bind(this);
    this.handleDownloadDocument = this.handleDownloadDocument.bind(this);
    this.handleExportClick = this.handleExportClick.bind(this);
    this.handleExportClose = this.handleExportClose.bind(this);
    this.handleExportCSV = this.handleExportCSV.bind(this);
    this.handleExportHL7 = this.handleExportHL7.bind(this);
    this.handleExportJSON = this.handleExportJSON.bind(this);
    this.handleExportXML = this.handleExportXML.bind(this);
    this.handleEditSummary = this.handleEditSummary.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.patient && this.props.patient !== prevProps.patient) {
      this.setState({ patientNote: this.props.patient.patientNote || "" });
    }
  }

  handleNoteChange(event) {
    this.setState({ patientNote: event.target.value });
  }

  async handleSaveNote() {
    const { patient } = this.props;
    if (!patient) return;

    this.setState({ savingNote: true });

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}`,
        {
          method: "PUT",
          headers: generalAuthTokenHeader(),
          body: JSON.stringify({ patientNote: this.state.patientNote }),
        }
      );

      if (response.ok) {
        userService.showNotification({
          title: "Success",
          message: "Patient note saved successfully",
          type: "success",
        });
        // Only refresh patient details, not the entire list
        if (this.props.onRefreshDetails) {
          this.props.onRefreshDetails();
        }
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to save patient note",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error saving note:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to save patient note",
        type: "danger",
      });
    } finally {
      this.setState({ savingNote: false });
    }
  }

  async handleDocumentUpload(event) {
    const { patient } = this.props;
    if (!patient || !event.target.files || event.target.files.length === 0)
      return;

    this.setState({ uploadingDocument: true });

    try {
      const formData = new FormData();
      for (let i = 0; i < event.target.files.length; i++) {
        formData.append("files", event.target.files[i]);
      }

      const headers = multimediaAuthTokenHeader();
      delete headers["Content-Type"];

      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/documents`,
        {
          method: "POST",
          headers: headers,
          body: formData,
        }
      );

      if (response.ok || response.status === 202) {
        userService.showNotification({
          title: "Success",
          message: "Document uploaded successfully",
          type: "success",
        });
        // Only refresh patient details, not the entire list
        if (this.props.onRefreshDetails) {
          this.props.onRefreshDetails();
        }
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to upload document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to upload document",
        type: "danger",
      });
    } finally {
      this.setState({ uploadingDocument: false });
      event.target.value = "";
    }
  }

  openDeleteConfirm() {
    this.setState({ confirmDeleteOpen: true });
  }

  closeDeleteConfirm = () => {
    this.setState({ confirmDeleteOpen: false });
  };

  async handleDeletePatient() {
    const { patient, onDelete } = this.props;
    if (!patient) return;

    this.setState({ deletingPatient: true });

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}`,
        {
          method: "DELETE",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok || response.status === 204) {
        userService.showNotification({
          title: "Success",
          message: "Patient deleted successfully",
          type: "success",
        });
        this.setState({ confirmDeleteOpen: false });
        onDelete(patient.id);
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to delete patient",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to delete patient",
        type: "danger",
      });
    } finally {
      this.setState({ deletingPatient: false });
    }
  }

  openDeleteDocConfirm(documentId) {
    this.setState({ confirmDeleteDocOpen: true, deleteDocumentId: documentId });
  }

  closeDeleteDocConfirm = () => {
    this.setState({ confirmDeleteDocOpen: false, deleteDocumentId: null });
  };

  async handleDeleteDocument() {
    const { patient } = this.props;
    const { deleteDocumentId } = this.state;
    if (!patient || !deleteDocumentId) return;

    this.setState({ deletingDocument: true });

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/documents/${deleteDocumentId}`,
        {
          method: "DELETE",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok || response.status === 204) {
        userService.showNotification({
          title: "Success",
          message: "Document deleted successfully",
          type: "success",
        });
        this.setState({ confirmDeleteDocOpen: false, deleteDocumentId: null });
        // Only refresh patient details, not the entire list
        if (this.props.onRefreshDetails) {
          this.props.onRefreshDetails();
        }
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to delete document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to delete document",
        type: "danger",
      });
    } finally {
      this.setState({ deletingDocument: false });
    }
  }

  async handleViewSummary() {
    const { patient, openContentViewer } = this.props;
    if (!patient) return;

    this.setState({ loadingAISummary: true });

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/summary`,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const summaryHtml = data.summaryHtml || "<p>No summary available</p>";
        
        // Use parent's content viewer instead of slide panel
        if (openContentViewer) {
          openContentViewer(summaryHtml, "html", "AI Referral Insights");
        }
        this.setState({ loadingAISummary: false });
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to load AI summary",
          type: "danger",
        });
        this.setState({ loadingAISummary: false });
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      userService.showNotification({
        title: "Error",
        message: "Error loading AI summary",
        type: "danger",
      });
      this.setState({ loadingAISummary: false });
    }
  }

  async handleDownloadPdf() {
    const { patient } = this.props;
    if (!patient) return;

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/download-pdf`,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `patient_${patient.id}_summary.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to download PDF",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to download PDF",
        type: "danger",
      });
    }
  }

  handleViewDocument(doc) {
    const { patient, openContentViewer } = this.props;
    if (!patient || !doc.id) return;

    this.setState({ loadingDocumentId: doc.id });

    // Get the document from the API
    fetch(
      `${apiPath.externalPatients}/${patient.id}/documents/${doc.id}/download`,
      {
        method: "GET",
        headers: generalAuthTokenHeader(),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch document");
        }
        return response.blob();
      })
      .then((blob) => {
        const fileName = doc.uploadName || doc.name || doc.fileName || `Document ${doc.id}`;
        const fileExtension = fileName.split(".").pop().toLowerCase();

        // Determine content type based on file extension
        let contentType = "";
        let content = "";

        if (
          ["jpg", "jpeg", "jfif", "jpe", "png", "gif", "bmp", "webp", "svg"].includes(
            fileExtension
          )
        ) {
          contentType = "image";
          content = URL.createObjectURL(blob);
          if (openContentViewer) {
            openContentViewer(content, contentType, fileName);
          }
          this.setState({ loadingDocumentId: null });
        } else if (fileExtension === "pdf") {
          contentType = "pdf";
          content = URL.createObjectURL(blob);
          if (openContentViewer) {
            openContentViewer(content, contentType, fileName);
          }
          this.setState({ loadingDocumentId: null });
        } else if (["doc", "docx"].includes(fileExtension)) {
          contentType = "docx";
          // Convert blob to base64 for DOCX conversion
          const reader = new FileReader();
          reader.onloadend = () => {
            if (openContentViewer) {
              openContentViewer(reader.result, contentType, fileName);
            }
            this.setState({ loadingDocumentId: null });
          };
          reader.readAsDataURL(blob);
        } else {
          // For unsupported types, just download
          userService.showNotification({
            title: "Info",
            message: "This file type cannot be previewed. Downloading instead...",
            type: "info",
          });
          this.handleDownloadDocument(doc);
          this.setState({ loadingDocumentId: null });
        }
      })
      .catch((error) => {
        console.error("Error viewing document:", error);
        userService.showNotification({
          title: "Error",
          message: error.message || "Failed to view document",
          type: "danger",
        });
        this.setState({ loadingDocumentId: null });
      });
  }

  async handleDownloadDocument(doc) {
    const { patient } = this.props;
    if (!patient || !doc.id) return;

    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/documents/${doc.id}/download`,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.uploadName || `document_${doc.id}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to download document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to download document",
        type: "danger",
      });
    }
  }

  formatDate(dateString) {
    if (!dateString) return "N/A";
    return moment(dateString).format("MM/DD/YYYY");
  }

  // Export Menu Handlers
  handleExportClick(event) {
    this.setState({ exportMenuAnchor: event.currentTarget });
  }

  handleExportClose() {
    this.setState({ exportMenuAnchor: null });
  }

  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  getPatientExportData() {
    const { patient } = this.props;
    if (!patient) return null;

    return {
      id: patient.id || "",
      firstName: patient.firstName || "",
      lastName: patient.lastName || "",
      dateOfBirth: patient.dateOfBirth || "",
      gender: patient.gender || "",
      insurance: patient.insurance || "",
      insurancePolicyNum: patient.insurancePolicyNum || "",
      primaryDiagnosis: patient.primaryDiagnosis || "",
      address: patient.address || "",
      city: patient.city || "",
      zip: patient.zip || "",
      phone: patient.phone || "",
      referringFacility: patient.referringFacility || "",
      referringPhysicianNpi: patient.referringPhysicianNpi || "",
      estimateDischargeDate: patient.estimateDischargeDate || "",
      patientNote: patient.patientNote || "",
      createdAt: patient.createdAt || "",
      medicalDetailJson: patient.medicalDetailJson || null,
    };
  }

  handleExportCSV() {
    const data = this.getPatientExportData();
    if (!data) return;

    const headers = [
      "PatientID", "FirstName", "LastName", "DOB", "Gender", "Insurance",
      "PolicyNumber", "Diagnosis", "Address", "City", "Zip", "Phone",
      "ReferringFacility", "PhysicianNPI", "DischargeDate", "Notes", "CreatedDate"
    ];

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value) => {
      const str = String(value || "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const values = [
      data.id, data.firstName, data.lastName, data.dateOfBirth, data.gender,
      data.insurance, data.insurancePolicyNum, data.primaryDiagnosis,
      data.address, data.city, data.zip, data.phone, data.referringFacility,
      data.referringPhysicianNpi, data.estimateDischargeDate, data.patientNote,
      data.createdAt
    ].map(escapeCSV);

    const csvContent = headers.join(",") + "\n" + values.join(",");
    const filename = `patient_${data.id}_${data.lastName}_${data.firstName}.csv`;

    this.downloadFile(csvContent, filename, "text/csv;charset=utf-8;");
    this.handleExportClose();
  }

  handleExportHL7() {
    const data = this.getPatientExportData();
    if (!data) return;

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      return moment(dateStr).format("YYYYMMDD");
    };

    const escapeHL7 = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/\|/g, "\\F\\")
        .replace(/\^/g, "\\S\\")
        .replace(/~/g, "\\R\\")
        .replace(/\\/g, "\\E\\")
        .replace(/&/g, "\\T\\")
        .replace(/\n/g, "~");
    };

    const now = moment().format("YYYYMMDDHHmmss");
    const genderCode = data.gender ? data.gender.charAt(0).toUpperCase() : "";

    // HL7 v2.5 ADT^A01 message format
    const segments = [
      `MSH|^~\\&|MAXMRJ|MAXMRJ_FACILITY|||${now}||ADT^A01|MSG${data.id}|P|2.5`,
      `PID|1||${data.id}||${data.lastName}^${data.firstName}||${formatDate(data.dateOfBirth)}|${genderCode}|||${data.address}^^${data.city}^^${data.zip}||${data.phone}`,
      `PV1|1|O||||${data.referringPhysicianNpi}^Referring^Physician`,
      data.primaryDiagnosis ? `DG1|1||${data.primaryDiagnosis}|||A` : "",
      data.insurance ? `IN1|1||${data.insurance}||||||${data.insurancePolicyNum}` : "",
      data.patientNote ? `NTE|1||${data.patientNote.replace(/\n/g, "~")}` : "",
    ].filter(Boolean);

    // Add medical detail segments if available
    if (data.medicalDetailJson) {
      // RXA segments for medications
      const medications = data.medicalDetailJson.Medications || [];
      medications.forEach((med, index) => {
        if (med.medication) {
          segments.push(
            `RXA|0|1|${now}||${escapeHL7(med.medication)}||${escapeHL7(med.dosageInstruction)}`
          );
        }
      });

      // OBX segment for observation
      const observation = data.medicalDetailJson.Observation;
      if (observation && (observation.text || observation.valueString)) {
        segments.push(
          `OBX|1|ST|${escapeHL7(observation.text)}||${escapeHL7(observation.valueString)}||||||F`
        );
      }

      // PR1 segment for procedure
      const procedure = data.medicalDetailJson.Procedure;
      if (procedure && procedure.text) {
        const procDate = procedure.performedDateTime ? formatDate(procedure.performedDateTime) : "";
        segments.push(
          `PR1|1||${escapeHL7(procedure.text)}|${escapeHL7(procedure.status)}|${procDate}|||${escapeHL7(procedure.note)}`
        );
      }
    }

    const hl7Content = segments.join("\r");
    const filename = `patient_${data.id}_${data.lastName}_${data.firstName}.hl7`;

    this.downloadFile(hl7Content, filename, "application/hl7-v2;charset=utf-8;");
    this.handleExportClose();
  }

  handleExportJSON() {
    const data = this.getPatientExportData();
    if (!data) return;

    const jsonExport = {
      exportInfo: {
        format: "MaxMRJ Patient Export",
        version: "1.0",
        exportedAt: new Date().toISOString(),
        source: "MaxMRJ Healthcare System"
      },
      patient: {
        id: data.id,
        demographics: {
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
        },
        contact: {
          address: data.address,
          city: data.city,
          zip: data.zip,
          phone: data.phone,
        },
        insurance: {
          name: data.insurance,
          policyNumber: data.insurancePolicyNum,
        },
        clinical: {
          primaryDiagnosis: data.primaryDiagnosis,
          estimatedDischargeDate: data.estimateDischargeDate,
          notes: data.patientNote,
          medicalDetails: data.medicalDetailJson ? {
            medications: data.medicalDetailJson.Medications || [],
            observation: data.medicalDetailJson.Observation || null,
            procedure: data.medicalDetailJson.Procedure || null,
          } : null,
        },
        referral: {
          referringFacility: data.referringFacility,
          referringPhysicianNpi: data.referringPhysicianNpi,
        },
        metadata: {
          createdAt: data.createdAt,
        }
      }
    };

    const jsonContent = JSON.stringify(jsonExport, null, 2);
    const filename = `patient_${data.id}_${data.lastName}_${data.firstName}.json`;

    this.downloadFile(jsonContent, filename, "application/json;charset=utf-8;");
    this.handleExportClose();
  }

  handleExportXML() {
    const data = this.getPatientExportData();
    if (!data) return;

    const escapeXML = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    };

    // Build medications section if available
    let medicationsSection = "";
    if (data.medicalDetailJson && data.medicalDetailJson.Medications && data.medicalDetailJson.Medications.length > 0) {
      const medicationRows = data.medicalDetailJson.Medications.map(med =>
        `                <tr><td>${escapeXML(med.medication)}</td><td>${escapeXML(med.dosageInstruction)}</td></tr>`
      ).join("\n");

      medicationsSection = `
      <component>
        <section>
          <title>Medications</title>
          <text>
            <table>
              <thead>
                <tr><th>Medication</th><th>Dosage Instructions</th></tr>
              </thead>
              <tbody>
${medicationRows}
              </tbody>
            </table>
          </text>
        </section>
      </component>`;
    }

    // Build observation section if available
    let observationSection = "";
    if (data.medicalDetailJson && data.medicalDetailJson.Observation &&
        (data.medicalDetailJson.Observation.text || data.medicalDetailJson.Observation.valueString)) {
      const obs = data.medicalDetailJson.Observation;
      observationSection = `
      <component>
        <section>
          <title>Observations</title>
          <text>
            <table>
              <tbody>
                <tr><td>Type</td><td>${escapeXML(obs.text)}</td></tr>
                <tr><td>Value</td><td>${escapeXML(obs.valueString)}</td></tr>
              </tbody>
            </table>
          </text>
        </section>
      </component>`;
    }

    // Build procedure section if available
    let procedureSection = "";
    if (data.medicalDetailJson && data.medicalDetailJson.Procedure && data.medicalDetailJson.Procedure.text) {
      const proc = data.medicalDetailJson.Procedure;
      procedureSection = `
      <component>
        <section>
          <title>Procedures</title>
          <text>
            <table>
              <tbody>
                <tr><td>Procedure</td><td>${escapeXML(proc.text)}</td></tr>
                <tr><td>Status</td><td>${escapeXML(proc.status)}</td></tr>
                <tr><td>Date</td><td>${escapeXML(proc.performedDateTime)}</td></tr>
                <tr><td>Notes</td><td>${escapeXML(proc.note)}</td></tr>
              </tbody>
            </table>
          </text>
        </section>
      </component>`;
    }

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<ClinicalDocument xmlns="urn:hl7-org:v3">
  <realmCode code="US"/>
  <typeId root="2.16.840.1.113883.1.3" extension="POCD_HD000040"/>
  <title>Patient Clinical Summary</title>
  <effectiveTime value="${moment().format("YYYYMMDDHHmmss")}"/>
  <recordTarget>
    <patientRole>
      <id extension="${escapeXML(data.id)}" root="MaxMRJ"/>
      <addr>
        <streetAddressLine>${escapeXML(data.address)}</streetAddressLine>
        <city>${escapeXML(data.city)}</city>
        <postalCode>${escapeXML(data.zip)}</postalCode>
      </addr>
      <telecom value="tel:${escapeXML(data.phone)}"/>
      <patient>
        <name>
          <given>${escapeXML(data.firstName)}</given>
          <family>${escapeXML(data.lastName)}</family>
        </name>
        <administrativeGenderCode code="${escapeXML(data.gender ? data.gender.charAt(0).toUpperCase() : "")}" displayName="${escapeXML(data.gender)}"/>
        <birthTime value="${data.dateOfBirth ? moment(data.dateOfBirth).format("YYYYMMDD") : ""}"/>
      </patient>
    </patientRole>
  </recordTarget>
  <component>
    <structuredBody>
      <component>
        <section>
          <title>Insurance Information</title>
          <text>
            <table>
              <tbody>
                <tr><td>Insurance</td><td>${escapeXML(data.insurance)}</td></tr>
                <tr><td>Policy Number</td><td>${escapeXML(data.insurancePolicyNum)}</td></tr>
              </tbody>
            </table>
          </text>
        </section>
      </component>
      <component>
        <section>
          <title>Diagnosis</title>
          <text>${escapeXML(data.primaryDiagnosis)}</text>
        </section>
      </component>
      <component>
        <section>
          <title>Referral Information</title>
          <text>
            <table>
              <tbody>
                <tr><td>Referring Facility</td><td>${escapeXML(data.referringFacility)}</td></tr>
                <tr><td>Referring Physician NPI</td><td>${escapeXML(data.referringPhysicianNpi)}</td></tr>
                <tr><td>Estimated Discharge Date</td><td>${escapeXML(data.estimateDischargeDate)}</td></tr>
              </tbody>
            </table>
          </text>
        </section>
      </component>
      <component>
        <section>
          <title>Notes</title>
          <text>${escapeXML(data.patientNote)}</text>
        </section>
      </component>${medicationsSection}${observationSection}${procedureSection}
    </structuredBody>
  </component>
</ClinicalDocument>`;

    const filename = `patient_${data.id}_${data.lastName}_${data.firstName}.xml`;

    this.downloadFile(xmlContent, filename, "application/xml;charset=utf-8;");
    this.handleExportClose();
  }

  handleEditSummary() {
    this.setState({ showReferralSummary: true });
  }

  handleCloseReferralSummary = () => {
    this.setState({ showReferralSummary: false });
    // Refresh data after editing
    if (this.props.onRefreshDetails) {
      this.props.onRefreshDetails();
    }
  };

  render() {
    const { patient, loading, onEdit, contentViewerOpen } = this.props;
    const {
      patientNote,
      savingNote,
      uploadingDocument,
      deletingPatient,
      deletingDocument,
      confirmDeleteOpen,
      confirmDeleteDocOpen,
      loadingAISummary,
      loadingDocumentId,
      tooltipAIInsightsOpen,
      exportMenuAnchor,
      showReferralSummary,
    } = this.state;

    if (loading) {
      return (
        <div className="patient-details-panel loading">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        </div>
      );
    }

    if (!patient) {
      return (
        <div className="patient-details-panel empty">
          <div className="select-patient-text">
            <span>Select Intake</span>
            <span>for View Details</span>
          </div>
        </div>
      );
    }

    const insuranceName = patient.insurance || "N/A";
    const documents = patient.uploadedDocuments || [];
    const hasDocuments = documents.length > 0;
    const aiSummaryStatus = patient.aiReferralSummaryStatus || "NONE";
    const isAIInsightsDisabled = aiSummaryStatus === "NONE";

    return (
      <div
        className={`patient-details-panel ${
          contentViewerOpen ? "ai-panel-open" : ""
        }`}
      >
        {/* Main Content */}
        <div className="panel-main-content">
          {/* Header */}
          <Box className="patient-header">
            <div className="patient-name-header">
              Patient Name: {patient.firstName} {patient.lastName}
            </div>
            <div className="header-actions">
              <Button
                className="export-btn"
                onClick={this.handleExportClick}
                startIcon={<GetAppIcon />}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={this.handleExportClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem onClick={this.handleExportCSV}>CSV</MenuItem>
                <MenuItem onClick={this.handleExportHL7}>HL7</MenuItem>
                <MenuItem onClick={this.handleExportJSON}>JSON</MenuItem>
                <MenuItem onClick={this.handleExportXML}>XML (Clinical Summary)</MenuItem>
              </Menu>
              <IconButton
                className="edit-btn"
                onClick={() => onEdit(patient)}
                title="Edit Patient"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                className="delete-btn"
                onClick={() => this.openDeleteConfirm()}
                title="Delete Patient"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </Box>

          {/* Patient Info Grid */}
          <Box className="patient-info-grid">
            <div className="info-item">
              <span className="info-label">DOB</span>
              <span className="info-value">
                {this.formatDate(patient.dateOfBirth)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Created On</span>
              <span className="info-value">
                {this.formatDate(patient.createdAt)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Gender</span>
              <span className="info-value">{patient.gender || "N/A"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Insurance</span>
              <span className="info-value">{insuranceName}</span>
            </div>
          </Box>

          {/* AI Insights and Documents */}
          <Box className="ai-insights-documents-section">
            {/* AI Referral Insights Section - Styled like Referral section */}
            <Box
              className={`ai-insights-section ${
                isAIInsightsDisabled ? "disabled" : ""
              }`}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                className="ai-insights-box"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  className="ai-insights-box-inner"
                >
                  <span className="crown-icon-wrapper">
                    <img
                      src={CrownIcon}
                      alt="Crown Icon"
                      className="crown-icon"
                    />
                  </span>
                  <span className="ai-insights-title">
                    AI Referral Insights
                  </span>
                  <ClickAwayListener
                    onClickAway={() =>
                      this.setState({ tooltipAIInsightsOpen: false })
                    }
                  >
                    <Tooltip
                      title="AI-generated clinical support tool for reference only. Not a medical record."
                      placement="top"
                      arrow
                      open={tooltipAIInsightsOpen}
                      onClose={() =>
                        this.setState({ tooltipAIInsightsOpen: false })
                      }
                      onOpen={() =>
                        this.setState({ tooltipAIInsightsOpen: true })
                      }
                      disableFocusListener
                      disableTouchListener
                    >
                      <InfoOutlinedIcon
                        className="info-icon"
                        onTouchStart={() =>
                          this.setState({ tooltipAIInsightsOpen: true })
                        }
                        onMouseEnter={() =>
                          this.setState({ tooltipAIInsightsOpen: true })
                        }
                        onMouseLeave={() =>
                          this.setState({ tooltipAIInsightsOpen: false })
                        }
                      />
                    </Tooltip>
                  </ClickAwayListener>
                  <span className="premium-badge">PREMIUM</span>
                </Box>

                {isAIInsightsDisabled ? (
                  <Box className="ai-status-badge disabled-badge">N/A</Box>
                ) : aiSummaryStatus === "PENDING" ? (
                  <Box className="ai-status-badge pending-badge">
                    Generating
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={1} className="ai-action-buttons">
                    <span
                      style={{
                        backgroundColor: "#796D54",
                        color: "#FFFFFF",
                        padding: "2px 14px",
                        borderRadius: 16,
                        fontSize: 12,
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        marginRight: 8,
                      }}
                      onClick={this.handleViewSummary}
                    >
                      Try Now !
                    </span>
                    <IconButton
                      onClick={this.handleEditSummary}
                      title="Edit Summary"
                      className="ai-edit-btn"
                    >
                      <EditIcon style={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      onClick={this.handleViewSummary}
                      disabled={loadingAISummary}
                      title="View Summary"
                      className="ai-view-btn"
                    >
                      {loadingAISummary ? (
                        <CircularProgress size={20} style={{ color: "#333" }} />
                      ) : (
                        <img src={EyeIcon} alt="View" className="action-icon" />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={this.handleDownloadPdf}
                      title="Download PDF"
                      className="ai-download-btn"
                    >
                      <img
                        src={DownloadIcon}
                        alt="Download"
                        className="action-icon"
                      />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Documents Section */}
            <Box className="documents-section">
              <div className="section-header">
                <span className="section-title">Documents</span>
                <input
                  type="file"
                  id="document-upload"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={this.handleDocumentUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="document-upload">
                  <Button
                    component="span"
                    className="add-document-btn"
                    disabled={uploadingDocument}
                  >
                    {uploadingDocument ? (
                      <CircularProgress size={16} />
                    ) : (
                      <>
                        <AddIcon /> Add Document
                      </>
                    )}
                  </Button>
                </label>
              </div>

              <div className="documents-list">
                {documents.length === 0 ? (
                  <div className="no-documents">
                    No documents have been uploaded for this patient yet
                  </div>
                ) : (
                  documents.map((doc, index) => (
                    <div key={doc.id || index} className="document-item">
                      <DescriptionIcon className="doc-icon" />
                      <span className="doc-name">
                        {doc.uploadName ||
                          doc.name ||
                          doc.fileName ||
                          `Document ${index + 1}`}
                      </span>
                      <div className="doc-actions">
                        <IconButton
                          size="small"
                          onClick={() => this.handleViewDocument(doc)}
                          disabled={loadingDocumentId === doc.id}
                          title="View Document"
                          className="doc-action-btn"
                        >
                          {loadingDocumentId === doc.id ? (
                            <CircularProgress size={16} style={{ color: "#666" }} />
                          ) : (
                            <img
                              src={EyeIcon}
                              alt="View"
                              className="doc-action-icon"
                            />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.openDeleteDocConfirm(doc.id)}
                          title="Delete Document"
                          className="doc-action-btn"
                        >
                          <img
                            src={DeleteIconSvg}
                            alt="Delete"
                            className="doc-action-icon"
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.handleDownloadDocument(doc)}
                          title="Download Document"
                          className="doc-action-btn"
                        >
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Box>
          </Box>

          {/* Notes Section */}
          <Box className="notes-section">
            <div className="section-header">
              <span className="section-title">Add Note for patient</span>
            </div>
            <div className="textarea-box">
              <TextField
                multiline
                rows={5}
                variant="outlined"
                fullWidth
                placeholder="Type your note here....."
                value={patientNote}
                onChange={this.handleNoteChange}
                className="note-input"
              />
              <Button
                className="save-note-btn"
                onClick={this.handleSaveNote}
                disabled={savingNote}
              >
                {savingNote ? <CircularProgress size={20} /> : "Save"}
              </Button>
            </div>
          </Box>
        </div>

        {/* Delete Patient Confirmation Modal */}
        <ConfirmModal
          title="Delete Patient"
          message="Are you sure you want to delete this patient? This action cannot be undone."
          confirmModalStatus={confirmDeleteOpen}
          confirmModalClose={this.closeDeleteConfirm}
          submit={this.handleDeletePatient}
          loading={deletingPatient}
        />

        {/* Delete Document Confirmation Modal */}
        <ConfirmModal
          title="Delete Document"
          message="Are you sure you want to delete this document? This action cannot be undone."
          confirmModalStatus={confirmDeleteDocOpen}
          confirmModalClose={this.closeDeleteDocConfirm}
          submit={this.handleDeleteDocument}
          loading={deletingDocument}
        />

        {/* AI Referral Insights Editor Modal */}
        <ReferralSummary
          open={showReferralSummary}
          onClose={this.handleCloseReferralSummary}
          patientId={patient?.id}
          apiGetUrl={`${apiPath.externalPatients}/${patient?.id}/summary`}
          apiPatchUrl={`${apiPath.externalPatients}/${patient?.id}/regenerate-pdf`}
          apiDownloadUrl={`${apiPath.externalPatients}/${patient?.id}/download-pdf`}
          hideDownloadButton={true}
        />
      </div>
    );
  }
}

export default PatientDetailsPanel;
